import { pence, subPence } from '@cf/shared';
import { assertReviewed } from './review.js';
import { excessOver, minPence, mulBps } from './bps.js';
/**
 * Pension allowance arithmetic. Relief itself arrives through incomeTax()
 * (relief at source: contributions are grossed up and extend the basic band),
 * so this module handles the LIMITS: annual allowance with taper and MPAA,
 * and the tax-free lump sum against the Lump Sum Allowance.
 */
/** Gross contribution from a net relief-at-source payment (net × 100/80). */
export function grossFromNetContribution(rules, net) {
    assertReviewed(rules);
    if (!Number.isSafeInteger(net) || net < 0) {
        throw new RangeError('Net contribution must be a non-negative integer of pence');
    }
    const basicBps = rules.incomeTax.basicRateBps;
    return pence(Math.trunc((net * 10_000) / (10_000 - basicBps)));
}
/**
 * The most a person can contribute with tax relief this year: the greater of
 * their relevant UK earnings and the £3,600 gross non-earner basic amount, but
 * never above the (tapered/MPAA-adjusted) annual allowance. This is what lets a
 * low- or non-earner still pay £3,600 gross into a pension with relief.
 */
export function relievableContributionLimit(rules, relevantEarnings, effectiveAllowance) {
    assertReviewed(rules);
    const floor = rules.pension.nonEarnerAnnualAllowanceFloor ?? pence(0);
    const earningsLimited = pence(Math.max(relevantEarnings, floor));
    return minPence(earningsLimited, effectiveAllowance);
}
/**
 * Annual allowance available including carry-forward of unused allowance from
 * up to the previous three tax years (an OPTIONAL modelling flag). Pass the
 * unused allowance amounts (oldest first); this simply sums the current
 * effective allowance and the provided carry-forward.
 */
export function annualAllowanceWithCarryForward(rules, effectiveAllowance, carriedForward) {
    assertReviewed(rules);
    const cf = carriedForward.slice(-3).reduce((a, v) => a + Math.max(0, v), 0);
    return pence(effectiveAllowance + cf);
}
/**
 * The effective annual allowance for one person: tapered £1 per £2 of
 * adjusted income over the limit (never below the minimum), and capped at the
 * MPAA once triggered. Taper applies only when BOTH the threshold-income and
 * adjusted-income limits are exceeded.
 */
export function effectiveAnnualAllowance(rules, input) {
    assertReviewed(rules);
    const p = rules.pension;
    let allowance = p.annualAllowance;
    if (input.thresholdIncome > p.taperThresholdIncome &&
        input.adjustedIncome > p.taperAdjustedIncome) {
        const reduction = pence(Math.trunc(excessOver(input.adjustedIncome, p.taperAdjustedIncome) / 2));
        allowance = pence(Math.max(p.taperMinimumAllowance, allowance - reduction));
    }
    if (input.mpaaTriggered) {
        allowance = minPence(allowance, p.moneyPurchaseAnnualAllowance);
    }
    return allowance;
}
/**
 * Contributions above the effective allowance. The excess is charged at the
 * person's marginal rate via an annual-allowance charge; the projection
 * treats it at household-estimate level (carry-forward of unused allowance
 * from the previous three years is NOT modelled and is warned about).
 */
export function annualAllowanceExcess(rules, totalGrossContributions, effectiveAllowance) {
    assertReviewed(rules);
    return excessOver(totalGrossContributions, effectiveAllowance);
}
export function taxFreeLumpSum(rules, input) {
    assertReviewed(rules);
    const p = rules.pension;
    if (!Number.isSafeInteger(input.crystallisedValue) || input.crystallisedValue < 0) {
        throw new RangeError('Crystallised value must be a non-negative integer of pence');
    }
    const lsaRemaining = excessOver(p.lumpSumAllowance, input.lumpSumAllowanceUsed);
    const quarter = mulBps(input.crystallisedValue, p.taxFreeLumpSumFractionBps);
    const taxFree = minPence(quarter, lsaRemaining);
    return {
        taxFreeLumpSum: taxFree,
        taxableResidue: subPence(input.crystallisedValue, taxFree),
        lumpSumAllowanceRemaining: subPence(lsaRemaining, taxFree),
    };
}
//# sourceMappingURL=pension.js.map